import * as React from 'react';
import { cn } from '@/utils/utils';
import { FIELD, fieldLook, type FieldLook } from '@/components/ui/recipes';
import { Chip } from '@/components/ui/chip';

interface TagFieldProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Shown while there are no tags. */
  placeholder?: string;
  /** Splits what's typed or pasted on this pattern, one tag per part (a list of emails); left out, it is one tag. */
  splitOn?: RegExp;
  /** The most tags it takes; once full it takes no more typing. */
  maxTags?: number;
  /** A required field that is empty or wrong. */
  invalid?: boolean;
  /** Filled (a form's) or outlined; left out, filled. */
  look?: FieldLook;
  className?: string;
}

/** Lureo's tag field: a field holding its tags as removable chips. Enter or a comma adds what's typed; Backspace on nothing removes the last tag. */
export function TagField({ value, onChange, placeholder = 'Type a tag and press Enter', splitOn, maxTags, invalid = false, look, className }: TagFieldProps) {
  const [draft, setDraft] = React.useState('');
  const room = maxTags === undefined ? Infinity : Math.max(0, maxTags - value.length);
  const full = room === 0;

  const commit = () => {
    const additions: string[] = [];
    for (const part of splitOn ? draft.split(splitOn) : [draft]) {
      const tag = part.trim();
      if (!tag || value.includes(tag) || additions.includes(tag)) continue;
      if (additions.length >= room) break;
      additions.push(tag);
    }
    if (additions.length > 0) onChange([...value, ...additions]);
    setDraft('');
  };

  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));

  return (
    <div className={cn(FIELD, fieldLook('md', look), 'flex min-h-10 flex-wrap items-center gap-1.5 px-2 py-1.5 focus-within:ring-2 focus-within:ring-lureo-accent/70', invalid && 'border-lureo-danger-ink', className)}>
      {value.map((tag, i) => (
        <Chip key={`${tag}-${i}`} size="xs" label={tag} onRemove={() => remove(i)} />
      ))}
      <input
        type="text"
        value={draft}
        disabled={full}
        onChange={event => setDraft(event.target.value)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
            event.preventDefault();
            remove(value.length - 1);
          }
        }}
        onBlur={commit}
        placeholder={full || value.length > 0 ? '' : placeholder}
        className="min-w-32 flex-1 border-0 bg-transparent py-1 text-lureo-14 leading-[1.3] text-lureo-ink outline-hidden placeholder:text-lureo-ink-subtlest disabled:cursor-not-allowed"
      />
    </div>
  );
}
